import { useState, useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

// Session-level cache for admin status (survives tab switches)
const adminCache = {
  checked: false,
  isAdmin: false,
  userId: null as string | null,
};

export const useAdminStatus = () => {
  const { user: authUser, loading: authLoading } = useAuth();
  const [isAdmin, setIsAdmin] = useState(adminCache.isAdmin);
  const [loading, setLoading] = useState(!adminCache.checked);
  const hasChecked = useRef(adminCache.checked);

  useEffect(() => {
    // Don't resolve admin status until the session has hydrated.
    if (authLoading) {
      setLoading(true);
      return;
    }

    // Skip if already checked this session
    if (hasChecked.current && adminCache.checked && adminCache.userId === (authUser?.id ?? null)) {
      setIsAdmin(adminCache.isAdmin);
      setLoading(false);
      return;
    }

    const checkAdminStatus = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          adminCache.checked = true;
          adminCache.isAdmin = false;
          adminCache.userId = null;
          setIsAdmin(false);
          setLoading(false);
          return;
        }

        // If same user, use cached result
        if (adminCache.checked && adminCache.userId === user.id) {
          setIsAdmin(adminCache.isAdmin);
          setLoading(false);
          return;
        }

        const { data, error } = await supabase.rpc('has_role', {
          _user_id: user.id,
          _role: 'admin'
        });

        const result = !error && data;
        adminCache.checked = true;
        adminCache.isAdmin = result;
        adminCache.userId = user.id;
        hasChecked.current = true;
        setIsAdmin(result);
      } catch (error) {
        console.error('Error checking admin status:', error);
        setIsAdmin(false);
      } finally {
        setLoading(false);
      }
    };

    checkAdminStatus();
  }, [authLoading, authUser?.id]);

  return { isAdmin, loading };
};
